/**
 * One list for everything a site has recently been asked to do.
 *
 * "Recent checks" answered only for the mesh diagnostics — Read Signal, Check
 * State, Scan Mesh — because that is the feed it was built on. Everything else
 * we send a controller went to the command log on the Events tab, two clicks
 * away and in a different shape.
 *
 * Which splits the one question this panel exists to answer. An operator
 * standing in front of a block that is not watering reads "Read Signal ·
 * 8/9 node(s) reported RSSI" and learns nothing about the valve they opened
 * ninety seconds ago, which is sitting QUEUED because the site has not polled
 * since Tuesday. Both facts were recorded; only one was on screen, and it was
 * the one that did not matter.
 *
 * So the two feeds are merged chronologically here. The diagnostics rows pass
 * through untouched — the server already classifies their tone, with a
 * per-action staleness budget this side has no business second-guessing — and
 * the command rows are classified to match.
 *
 * `field_commands` is the source of both, so a diagnostic appears in each feed.
 * Deduped on `cmd_id`, keeping the server's richer row.
 */

import {
  relayDisplayName,
  type DiagnosticResult,
  type FieldCommand,
  type EspNowRouter,
} from '../services/espnowIrrigation';

export type ActivityTone = DiagnosticResult['tone'] | 'warn';

export interface ActivityRow {
  /** React key. A group's is its first command's cmd_id. */
  id: string;
  /** Check rows carry the server's classification and get the coverage note. */
  check: DiagnosticResult | null;
  /** The raw command type, for callers that switch on it. */
  action: string;
  label: string;
  /** Which valve or node, or null for a site-wide command. */
  target: string | null;
  /** What the site said back, or what we can say on its behalf. */
  detail: string | null;
  /** ISO timestamp of the row (a group's earliest). */
  at: string;
  tone: ActivityTone;
  /** How many commands this row stands for. 1 for everything but a burst. */
  count: number;
}

/**
 * How close together two commands of the same type must be queued to count as
 * one action.
 *
 * A schedule dispatch queues one SET_RELAY per relay in a tight loop —
 * `dispatchProgram` walks every step of every program due today — so a farm
 * with a few programs lays down a dozen or more commands inside a second. Left
 * ungrouped they are the whole list, every night, and the checks this panel was
 * built for scroll off the bottom.
 *
 * Twenty seconds rather than one: the loop awaits a round trip per insert, so a
 * large dispatch is not instantaneous, and the gap that actually separates a
 * dispatch from an operator's tap is minutes.
 */
export const BURST_GAP_SEC = 20;

/**
 * When a command still sitting QUEUED stops being pending and starts being
 * evidence.
 *
 * The bridge polls about every 15 s, so anything past this is not in progress —
 * the site is not collecting. Matches the server's default for a diagnostic;
 * the longer per-action budgets there exist for commands that take minutes to
 * RUN, and nothing in this set does.
 */
export const COMMAND_STALE_SEC = 180;

/**
 * What to call a command in the activity list.
 *
 * The diagnostics half is worded to match the buttons that queue it, so a row
 * and the control an operator just pressed read the same. The operational half
 * is worded for what it did to the farm rather than for the wire — "Valve run",
 * not SET_RELAY — because this list is read by somebody asking why a block is
 * dry, not by somebody reading the protocol.
 */
const LABEL: Record<string, string> = {
  CHECK_COORDINATOR: 'Check Coordinator',
  DISCOVER_NODES: 'Find nodes',
  READ_STATES: 'Check State',
  READ_SIGNAL: 'Read Signal',
  SCAN_MESH: 'Scan Mesh',
  SET_BROADCASTING: 'Enable Reporting',
  SET_NODE_NAME: 'Rename node',
  ADD_NODE: 'Add node by address',
  ALL_OFF: 'All Off',
  SET_RELAY: 'Valve run',
  CLEAR_RELAY: 'Valve stopped',
  RUN_PROGRAM: 'Program started',
  GET_STATUS: 'Status request',
  TIMESYNC: 'Clock sync',
  HEARTBEAT: 'Heartbeat',
  REMOVE_NODE: 'Node removed',
  OTA_UPDATE: 'Firmware update',
  UPDATE_BRIDGE: 'Bridge update',
};

/** Worst-first, so a group takes its tone from its worst member. */
const SEVERITY: Record<ActivityTone, number> = {
  error: 4, stale: 3, warn: 2, pending: 1, success: 0,
};

/** Only used when the site has said nothing of its own. */
const FALLBACK: Record<ActivityTone, string> = {
  success: 'Confirmed by the site.',
  error: 'Failed, and the site gave no reason.',
  warn: 'Stored, but the site reported it cannot act on it.',
  pending: 'Queued — waiting for the site to collect it.',
  stale: 'Still queued long after the site should have polled. It is probably offline.',
};

/** An unknown type keeps its wire name: inventing prose for it would be a guess. */
export function actionLabel(action: string): string {
  return LABEL[action] ?? action;
}

export function activityFallback(tone: ActivityTone): string {
  return FALLBACK[tone];
}

function ageSec(at: string, now: number): number {
  const t = Date.parse(at);
  return Number.isFinite(t) ? (now - t) / 1000 : 0;
}

/**
 * One command's outcome.
 *
 * `armed === false` is its own tone rather than a success or a failure: the
 * site took the command and answered that it cannot act on it — no clock, or a
 * zero-length window. Delivered, and the block still stays dry. Folding it into
 * either neighbouring state loses the only thing worth knowing about it.
 */
export function commandTone(cmd: FieldCommand, now: number): ActivityTone {
  if (cmd.status === 'ERROR') return 'error';
  if (cmd.armed === false) return 'warn';
  if (cmd.status === 'OK' || cmd.status === 'CONFIRMED') return 'success';
  return ageSec(cmd.created_at, now) > COMMAND_STALE_SEC ? 'stale' : 'pending';
}

function findRouter(routers: EspNowRouter[], mac: unknown): EspNowRouter | undefined {
  if (typeof mac !== 'string' || !mac) return undefined;
  return routers.find(x => x.router_mac.toUpperCase() === mac.toUpperCase());
}

function routerLabel(routers: EspNowRouter[], mac: unknown): string | null {
  if (typeof mac !== 'string' || !mac) return null;
  return findRouter(routers, mac)?.name || mac.slice(-6);
}

/** "for 15 min" — a manual run's window, which nothing else on the row states. */
function runWindow(sec: unknown): string | null {
  const s = Number(sec);
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s < 60) return `for ${Math.round(s)} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `for ${min} min`;
  const hrs = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `for ${hrs} h ${rem} min` : `for ${hrs} h`;
}

/**
 * What to call the thing one command acted on.
 *
 * A valve is named the way its node card names it, not as "relay 2" — the same
 * output answering to two different names on two pages is how an operator ends
 * up testing the wrong one.
 */
export function commandTarget(cmd: FieldCommand, routers: EspNowRouter[]): string | null {
  const p = cmd.payload || {};
  const node = routerLabel(routers, p.router_mac);
  const relay = Number(p.relay ?? p.relay_index);
  if (node && Number.isFinite(relay)) {
    return `${node} · ${relayDisplayName(null, findRouter(routers, p.router_mac)?.relay_labels, relay)}`;
  }
  if (node) return node;
  if (typeof p.version === 'string') return `v${p.version}`;
  // A program id is 36 characters nobody can read. Saying what kind of thing it
  // is beats printing it.
  if (p.program_id) return 'schedule program';
  return null;
}

/**
 * MACs reach us in three spellings — the program editor's, the node's own
 * report, and whatever an operator typed — so a relay is matched on the hex
 * alone. Comparing the strings makes one valve look like two, and the half a
 * filter misses is silently reported as "nothing was sent to this valve".
 */
const macKey = (m: unknown) => String(m ?? '').toUpperCase().replace(/[^0-9A-F]/g, '');

/**
 * Just the commands that addressed one relay.
 *
 * For the network map's node drawer, where the question is about a single
 * valve rather than a site: this node is linked to relay 2 on the pump-shed
 * node — what has that relay actually been asked to do?
 */
export function commandsForRelay(
  commands: FieldCommand[], routerMac: string, relayIndex: number,
): FieldCommand[] {
  const mac = macKey(routerMac);
  return commands.filter(c => {
    const p = c.payload || {};
    return macKey(p.router_mac) === mac && Number(p.relay ?? p.relay_index) === relayIndex;
  });
}

/**
 * Consecutive same-type commands queued together, oldest first within each run.
 *
 * Grouped on type and proximity alone, deliberately not on target: what makes a
 * schedule dispatch one action is that it went out as one, and its members are
 * by definition all different valves.
 */
function burst(commands: FieldCommand[]): FieldCommand[][] {
  const groups: FieldCommand[][] = [];
  for (const cmd of commands) {
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    const together = prev
      && prev.type === cmd.type
      && Math.abs(Date.parse(cmd.created_at) - Date.parse(prev.created_at)) / 1000 <= BURST_GAP_SEC;
    if (together) last.push(cmd);
    else groups.push([cmd]);
  }
  return groups;
}

/** "North 1, North 2, Pump +3 more" — enough to recognise, short enough to fit. */
function groupTargets(group: FieldCommand[], routers: EspNowRouter[]): string | null {
  const names = [...new Set(
    group.map(c => commandTarget(c, routers)).filter((n): n is string => !!n),
  )];
  if (names.length === 0) return null;
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
}

function groupDetail(group: FieldCommand[], tone: ActivityTone, now: number): string {
  if (group.length === 1) return group[0].detail || FALLBACK[tone];

  const confirmed = group.filter(c => commandTone(c, now) === 'success').length;
  if (confirmed === group.length) return `All ${group.length} confirmed by the site.`;

  // The first thing that went wrong, by name. A count alone says something
  // failed and leaves the operator to go and find out what.
  const bad = group.find(c => {
    const t = commandTone(c, now);
    return t === 'error' || t === 'warn';
  });
  const reason = bad?.detail ? ` ${bad.detail}` : '';
  return `${confirmed} of ${group.length} confirmed.${reason}`;
}

function toRow(group: FieldCommand[], routers: EspNowRouter[], now: number): ActivityRow {
  const tone = group
    .map(c => commandTone(c, now))
    .reduce<ActivityTone>((worst, t) => (SEVERITY[t] > SEVERITY[worst] ? t : worst), 'success');
  const first = group[0];

  // A lone SET_RELAY is somebody opening one valve, and how long for is the
  // thing they came back to check.
  const single = [
    commandTarget(first, routers),
    first.type === 'SET_RELAY' ? runWindow(first.payload?.duration_sec) : null,
  ].filter(Boolean).join(' · ');

  return {
    id: first.cmd_id,
    check: null,
    action: first.type,
    label: actionLabel(first.type),
    target: group.length === 1 ? (single || null) : groupTargets(group, routers),
    detail: groupDetail(group, tone, now),
    at: first.created_at,
    tone,
    count: group.length,
  };
}

function checkRow(r: DiagnosticResult): ActivityRow {
  return {
    id: r.cmd_id,
    check: r,
    action: r.action,
    label: actionLabel(r.action),
    target: null,
    detail: r.detail,
    at: r.created_at,
    tone: r.tone,
    count: 1,
  };
}

/** Merge the diagnostics feed and the command log into one newest-first list. */
export function buildActivity(input: {
  results: DiagnosticResult[];
  commands: FieldCommand[];
  routers: EspNowRouter[];
  now?: number;
  limit?: number;
}): ActivityRow[] {
  const { results, commands, routers } = input;
  const now = input.now ?? Date.now();

  const seen = new Set(results.map(r => r.cmd_id));
  const rest = commands
    .filter(c => !seen.has(c.cmd_id))
    .slice()
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

  const rows = [
    ...results.map(checkRow),
    ...burst(rest).map(g => toRow(g, routers, now)),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  return input.limit ? rows.slice(0, input.limit) : rows;
}
